import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Mail, MousePointer, Tag, DollarSign, Users } from 'lucide-react';
import { getAdminApiUrl } from '../utils/api';

interface UserEngagement {
  email: string;
  userId?: string;
  timestamp: string;
  discountAmount?: number;
  transactionId?: string;
}

interface CampaignAnalytics {
  campaignId: string;
  campaignName: string;
  solutionId?: string;
  status: string;
  metrics: {
    emailsSent: number;
    emailsOpened: number;
    emailsClicked: number;
    discountsUsed: number;
    revenue: number;
    openRate: string;
    clickRate: string;
    conversionRate: string;
  };
  userEngagement?: {
    usersWhoOpened: UserEngagement[];
    usersWhoClicked: UserEngagement[];
    usersWhoUsedDiscount: UserEngagement[];
  };
}

const CampaignAnalytics: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState<CampaignAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadAnalytics = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${getAdminApiUrl()}/admin/campaigns/${campaignId}/analytics`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      console.log('Analytics API response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Analytics API error:', errorText);
        throw new Error('Failed to load analytics');
      }
      
      const data = await response.json();
      console.log('Analytics data received:', data);
      setAnalytics(data);
    } catch (err: any) {
      console.error('Analytics load error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">Loading analytics...</div>
      </div>
    );
  }

  if (error || !analytics || !analytics.metrics) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error || 'Analytics not found'}
        </div>
      </div>
    );
  }

  const { metrics } = analytics;

  return (
    <div className="container mx-auto p-6">
      <button
        onClick={() => navigate('/admin/campaigns')}
        className="flex items-center gap-2 text-blue-600 hover:underline mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Campaigns
      </button>

      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">{analytics.campaignName}</h1>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            analytics.status === 'sent' ? 'bg-green-100 text-green-800' :
            analytics.status === 'sending' ? 'bg-blue-100 text-blue-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {analytics.status}
          </span>
          <span className="text-sm text-gray-600">Campaign ID: {analytics.campaignId}</span>
          {analytics.solutionId && (
            <span className="text-sm text-gray-600">Solution: {analytics.solutionId}</span>
          )}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Emails Sent</span>
            <Mail className="w-5 h-5 text-blue-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900">{metrics.emailsSent}</div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Open Rate</span>
            <TrendingUp className="w-5 h-5 text-green-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900">{metrics.openRate}</div>
          <div className="text-sm text-gray-500 mt-1">{metrics.emailsOpened} opened</div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Click Rate</span>
            <MousePointer className="w-5 h-5 text-purple-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900">{metrics.clickRate}</div>
          <div className="text-sm text-gray-500 mt-1">{metrics.emailsClicked} clicked</div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-orange-500">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Conversion Rate</span>
            <Tag className="w-5 h-5 text-orange-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900">{metrics.conversionRate}</div>
          <div className="text-sm text-gray-500 mt-1">{metrics.discountsUsed} used</div>
        </div>
      </div>

      {/* Revenue & Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="w-6 h-6 text-green-600" />
            <h2 className="text-xl font-semibold">Revenue Generated</h2>
          </div>
          <div className="text-4xl font-bold text-green-600 mb-2">
            ₹{metrics.revenue.toLocaleString('en-IN')}
          </div>
          <div className="text-sm text-gray-600">
            From {metrics.discountsUsed} discount redemptions
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold">Campaign Funnel</h2>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Emails Sent</span>
              <span className="font-semibold">{metrics.emailsSent}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full" style={{ width: '100%' }}></div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Opened</span>
              <span className="font-semibold">{metrics.emailsOpened}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-600 h-2 rounded-full" 
                style={{ width: metrics.emailsSent > 0 ? `${(metrics.emailsOpened / metrics.emailsSent) * 100}%` : '0%' }}
              ></div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Clicked</span>
              <span className="font-semibold">{metrics.emailsClicked}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-purple-600 h-2 rounded-full" 
                style={{ width: metrics.emailsSent > 0 ? `${(metrics.emailsClicked / metrics.emailsSent) * 100}%` : '0%' }}
              ></div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Converted</span>
              <span className="font-semibold">{metrics.discountsUsed}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-orange-600 h-2 rounded-full" 
                style={{ width: metrics.emailsSent > 0 ? `${(metrics.discountsUsed / metrics.emailsSent) * 100}%` : '0%' }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* User Engagement Details */}
      {analytics.userEngagement && (
        <div className="space-y-6">
          {/* Users Who Opened Emails */}
          {analytics.userEngagement.usersWhoOpened.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-green-600" />
                Users Who Opened Emails ({analytics.userEngagement.usersWhoOpened.length})
              </h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Opened At
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {analytics.userEngagement.usersWhoOpened.map((user, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(user.timestamp).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Users Who Clicked Links */}
          {analytics.userEngagement.usersWhoClicked.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <MousePointer className="w-5 h-5 text-purple-600" />
                Users Who Clicked Links ({analytics.userEngagement.usersWhoClicked.length})
              </h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Clicked At
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {analytics.userEngagement.usersWhoClicked.map((user, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(user.timestamp).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Users Who Used Discount */}
          {analytics.userEngagement.usersWhoUsedDiscount.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Tag className="w-5 h-5 text-orange-600" />
                Users Who Used Discount ({analytics.userEngagement.usersWhoUsedDiscount.length})
              </h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Discount Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Transaction ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Used At
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {analytics.userEngagement.usersWhoUsedDiscount.map((user, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ₹{user.discountAmount?.toLocaleString('en-IN') || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                          {user.transactionId}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(user.timestamp).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CampaignAnalytics;
